import React, { useState } from 'react';
import PropTypes from 'prop-types';
import {
  Divider,
  IconButton,
  Tooltip,
  CircularProgress
} from '@mui/material';
import ReactMarkdown from 'react-markdown';
import EditIcon from '@mui/icons-material/Edit';
import DownloadIcon from '@mui/icons-material/Download';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import DescriptionIcon from '@mui/icons-material/Description';
import TextSnippetIcon from '@mui/icons-material/TextSnippet';
import PrintIcon from '@mui/icons-material/Print';
import { formatDateTime } from '../../utils/dateUtils';
// Import document export functions
import { downloadAsPdf } from '../../utils/simplePdfGenerator';
import { downloadAsMarkdown, downloadAsDocx } from '../../utils/documentExporter';
import { Box, Typography } from '@mui/material';

/**
 * Custom renderer components for ReactMarkdown
 * Uses our design system Typography components
 */
const MarkdownComponents = {
  h1: (props) => <Typography variant="h3" color="primary" gutterBottom sx={{ width: '100%' }} {...props} />,
  h2: (props) => <Typography variant="h4" gutterBottom sx={{ mt: 4, width: '100%' }} {...props} />,
  h3: (props) => <Typography variant="h5" gutterBottom sx={{ mt: 3, width: '100%' }} {...props} />,
  h4: (props) => <Typography variant="h6" gutterBottom sx={{ mt: 2, width: '100%' }} {...props} />,
  h5: (props) => <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'bold', width: '100%' }} {...props} />,
  h6: (props) => <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 'bold', width: '100%' }} {...props} />,
  p: (props) => <Typography variant="body1" paragraph sx={{ width: '100%' }} {...props} />,
  a: (props) => <Typography component="a" color="primary" {...props} />,
  ul: (props) => <Box component="ul" sx={{ pl: 4, mb: 2, width: '100%' }} {...props} />,
  ol: (props) => <Box component="ol" sx={{ pl: 4, mb: 2, width: '100%' }} {...props} />,
  li: (props) => <Box component="li" sx={{ mb: 1 }} {...props} />,
  code: (props) => <Box component="code" sx={{ p: 0.5, backgroundColor: 'rgba(0, 0, 0, 0.04)', borderRadius: 1 }} {...props} />,
  // Add table styling for better readability
  table: (props) => <Box component="table" sx={{ width: '100%', borderCollapse: 'collapse', my: 2 }} {...props} />,
  thead: (props) => <Box component="thead" sx={{ backgroundColor: 'rgba(0,0,0,0.04)' }} {...props} />,
  tbody: (props) => <Box component="tbody" {...props} />,
  tr: (props) => <Box component="tr" sx={{ borderBottom: '1px solid rgba(0,0,0,0.12)' }} {...props} />,
  th: (props) => <Box component="th" sx={{ p: 1.5, textAlign: 'left', fontWeight: 'bold' }} {...props} />,
  td: (props) => <Box component="td" sx={{ p: 1.5 }} {...props} />
};

/**
 * DocumentPreview Component
 * Displays a document in a readable format with markdown support
 * 
 * @param {Object} props - Component props
 * @param {Object} props.document - Document data object
 * @param {Function} props.onEdit - Edit callback function
 * @param {Function} props.onDownload - Download callback function
 * @returns {React.ReactElement} DocumentPreview component
 */
const DocumentPreview = ({ document, onEdit, onDownload }) => {
  const [isGeneratingPdf, setIsGeneratingPdf] = useState(false);
  const [isGeneratingDocx, setIsGeneratingDocx] = useState(false);
  const [isGeneratingMarkdown, setIsGeneratingMarkdown] = useState(false);

  if (!document) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography variant="body1">No document to display</Typography>
      </Box>
    );
  }

  const handlePrint = () => {
    window.print();
  };

  // Handle PDF download with the html2pdf.js utility
  const handlePdfDownload = () => {
    try {
      setIsGeneratingPdf(true);

      // Small delay to show loading indicator
      setTimeout(() => {
        // Generate and download PDF directly without dialogs
        try {
          downloadAsPdf(document.content, document.title);
          // PDF generation is async, but we'll hide the indicator after a reasonable time
          setTimeout(() => {
            setIsGeneratingPdf(false);
          }, 2000); // Give enough time for PDF to generate
        } catch (error) {
          console.error('Error in PDF generation:', error);
          setIsGeneratingPdf(false);
        }
      }, 300);
    } catch (error) {
      console.error('Error downloading PDF:', error);
      setIsGeneratingPdf(false);
    }
  };

  // Handle DOCX download with the utility
  const handleDocxDownload = async () => {
    try {
      setIsGeneratingDocx(true);
      await downloadAsDocx(document.content, document.title);
    } catch (error) {
      console.error('Error downloading DOCX:', error);
      alert('Failed to generate DOCX. Please try again or use another format.');
    } finally {
      setIsGeneratingDocx(false);
    }
  };

  // Handle Markdown download with the utility
  const handleMarkdownDownload = async () => {
    try {
      setIsGeneratingMarkdown(true);
      await downloadAsMarkdown(document.content, document.title);
    } catch (error) {
      console.error('Error downloading Markdown:', error);
      alert('Failed to generate Markdown file. Please try again.');
    } finally {
      setIsGeneratingMarkdown(false);
    }
  };

  // Function to handle downloading document in different formats
  const handleDownloadFormat = (format) => {
    switch(format) {
      case 'pdf':
        handlePdfDownload();
        break;
      case 'docx':
        handleDocxDownload();
        break;
      case 'md':
        handleMarkdownDownload();
        break;
      default:
        console.error('Unknown format:', format);
    }
  };

  return (
    <Box sx={{ 
      width: '100%',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center'
    }}>
      {/* Document Header */}
      <Box sx={{ 
        mb: 3, 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        width: '100%',
        maxWidth: '1200px'
      }}>
        <Box>
          <Typography variant="h5" component="h2" gutterBottom>
            {document.title}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Version {document.version} • Last updated: {formatDateTime(document.updatedAt || document.createdAt)}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          {onEdit && (
            <Tooltip title="Edit Document">
              <IconButton onClick={onEdit} sx={{ border: '1px solid rgba(0, 0, 0, 0.12)' }}>
                <EditIcon />
              </IconButton>
            </Tooltip>
          )}
          
          <Tooltip title="Download as PDF">
            <IconButton
              onClick={() => handleDownloadFormat('pdf')}
              disabled={isGeneratingPdf}
              sx={{ border: '1px solid rgba(0, 0, 0, 0.12)' }}
            >
              {isGeneratingPdf ? (
                <CircularProgress size={24} color="error" />
              ) : (
                <PictureAsPdfIcon color="error" />
              )}
            </IconButton>
          </Tooltip>

          <Tooltip title="Download as DOCX">
            <IconButton
              onClick={() => handleDownloadFormat('docx')}
              disabled={isGeneratingDocx}
              sx={{ border: '1px solid rgba(0, 0, 0, 0.12)' }}
            >
              {isGeneratingDocx ? (
                <CircularProgress size={24} color="primary" />
              ) : (
                <DescriptionIcon color="primary" />
              )}
            </IconButton>
          </Tooltip>

          <Tooltip title="Download as Markdown">
            <IconButton
              onClick={() => handleDownloadFormat('md')}
              disabled={isGeneratingMarkdown}
              sx={{ border: '1px solid rgba(0, 0, 0, 0.12)' }}
            >
              {isGeneratingMarkdown ? (
                <CircularProgress size={24} />
              ) : (
                <TextSnippetIcon />
              )}
            </IconButton>
          </Tooltip>
          
          <Tooltip title="Print Document">
            <IconButton onClick={handlePrint} sx={{ border: '1px solid rgba(0, 0, 0, 0.12)' }}>
              <PrintIcon />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>
      
      <Divider sx={{ mb: 3, width: '100%', maxWidth: '1200px' }} />
      
      {/* Document Content */}
      <Box className="markdown-content" sx={{
        width: '100%',
        maxWidth: '1200px',
        height: 'calc(100vh - 320px)', // Adjust to fit the screen
        overflowY: 'auto', // Enable vertical scrolling
        overflowX: 'hidden',
        padding: '0 16px',
        // Custom scrollbar styling
        '&::-webkit-scrollbar': {
          width: '8px',
          backgroundColor: 'transparent',
        },
        '&::-webkit-scrollbar-track': {
          borderRadius: '10px',
          backgroundColor: 'rgba(0, 0, 0, 0.05)',
          marginTop: '5px',
          marginBottom: '5px',
        },
        '&::-webkit-scrollbar-thumb': {
          borderRadius: '10px',
          backgroundColor: 'rgba(0, 0, 0, 0.2)',
          '&:hover': {
            backgroundColor: 'rgba(25, 118, 210, 0.4)',
          },
        },
        '&::-webkit-scrollbar-corner': {
          backgroundColor: 'transparent',
        },
      }}>
        <ReactMarkdown components={MarkdownComponents}>
          {document.content}
        </ReactMarkdown>
      </Box>
    </Box>
  );
};

DocumentPreview.propTypes = {
  document: PropTypes.shape({
    title: PropTypes.string.isRequired,
    content: PropTypes.string.isRequired,
    version: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    createdAt: PropTypes.string,
    updatedAt: PropTypes.string
  }),
  onEdit: PropTypes.func,
  onDownload: PropTypes.func
};

export default DocumentPreview;